<template>
  <div class="ct-idtel-shell idtel-compact-shell">
    <div v-if="submitProgress" class="ct-idtel-progress">
      Corrigiendo paso {{ submitProgress.current }} de {{ submitProgress.total }}…
    </div>

    <section
      v-for="(micro, mIdx) in microSections"
      :key="micro.key"
      class="ct-idtel-compact-micro"
    >
      <div class="ct-idtel-compact-title">{{ micro.micro }}</div>
      <div class="ct-idtel-compact-domains">
        <div
          v-for="(domain, dIdx) in micro.domains"
          :key="domain.key"
          class="ct-idtel-domain-card"
        >
          <h4>{{ domain.name }}</h4>
          <div class="ct-idtel-items-grid">
            <div
              v-for="(val, rowIdx) in uiState[domain.key]"
              :key="rowIdx"
              class="ct-idtel-item-line"
              :class="[
                lineClass(val),
                { 'ct-guide-blink': isGuideActive(mIdx, dIdx, rowIdx) },
              ]"
            >
              <span class="ct-idtel-item-num">{{ rowIdx + 1 }}</span>
              <select
                class="ct-idtel-select"
                :class="selectClass(val)"
                :value="val"
                :disabled="corrected || loading"
                @mousedown="onSelectInteract"
                @click="onSelectInteract"
                @focus="onSelectInteract"
                @change="setItem(domain.key, rowIdx, $event.target.value)"
              >
                <option
                  v-for="opt in itemOptions(domain.stepIndex, rowIdx)"
                  :key="opt.value"
                  :value="opt.value"
                >
                  {{ opt.text }}
                </option>
              </select>
            </div>
          </div>
        </div>
      </div>
    </section>

    <div v-if="corrected" class="ct-idtel-result">
      <span>Correctas: {{ summary.correct }}</span>
      <span>Incorrectas: {{ summary.incorrect }}</span>
      <span>Omitidas: {{ summary.omitted }}</span>
      <span>Total: {{ summary.total }}</span>
    </div>
  </div>
</template>

<script>
import { IDTEL_DATA } from "../data/idtelData";
import {
  createIdtelUiState,
  buildIdtelStepsAnswers,
  parseIdtelStepsAnswers,
  summarizeIdtelState,
  idtelDomainKey,
  getIdtelItemOptions,
  getIdtelDomainSteps,
} from "../utils/idtelState";
import { idtelFlatIndex } from "../utils/testCorrectionGuide";
import { IDTEL_STEP_COUNT } from "../constants/testCatalog";
import {
  IDTEL_DEFAULT_ANSWER,
  IDTEL_OMITTED_ANSWER,
} from "../data/idtelSchema";
import { runIdtelCorrection } from "@/services/testCorrectionAdapter";

export default {
  name: "IdtelForm",
  inject: {
    corregirTestWorkspace: { default: null },
  },
  props: {
    testValue: { type: Number, required: true },
    age: { type: String, default: "" },
    initialAnswers: { type: Object, default: null },
  },
  data() {
    return {
      // Editar una corrección previa: los 15 pasos se guardaron como
      // {'step-N': {...}}, y parseIdtelStepsAnswers los vuelve a plegar en el
      // uiState por dominio. La edad la restaura el workspace (startEdit).
      uiState: this.initialAnswers
        ? parseIdtelStepsAnswers(this.initialAnswers)
        : createIdtelUiState(),
      corrected: false,
      loading: false,
      submitProgress: null,
    };
  },
  computed: {
    microSections() {
      const stepByKey = {};
      getIdtelDomainSteps().forEach((step) => {
        stepByKey[step.key] = step.stepIndex;
      });
      return IDTEL_DATA.map((micro, mIdx) => ({
        key: "micro-" + mIdx,
        micro: micro.micro,
        domains: micro.domains.map((domain, dIdx) => ({
          ...domain,
          key: idtelDomainKey(mIdx, dIdx),
          stepIndex: stepByKey[idtelDomainKey(mIdx, dIdx)],
        })),
      }));
    },
    summary() {
      return summarizeIdtelState(this.uiState);
    },
    toolbarLabel() {
      if (this.loading && this.submitProgress) {
        return (
          "Paso " +
          this.submitProgress.current +
          "/" +
          this.submitProgress.total
        );
      }
      return "Corregir test";
    },
    toolbarDisabled() {
      if (this.loading) return true;
      if (this.corrected) return true;
      return !this.age;
    },
  },
  watch: {
    toolbarLabel: { immediate: true, handler: "emitToolbarMeta" },
    toolbarDisabled: { immediate: true, handler: "emitToolbarMeta" },
    loading: { handler: "emitToolbarMeta" },
  },
  methods: {
    emitToolbarMeta() {
      this.$emit("toolbar-meta", {
        label: this.toolbarLabel,
        disabled: this.toolbarDisabled,
        loading: this.loading,
      });
    },
    itemOptions(stepIndex, rowIdx) {
      return getIdtelItemOptions(stepIndex, rowIdx);
    },
    // Tres estados, no cuatro: correcta (blanca), omitida (amarilla) y
    // cualquier otra cosa, que es un distractor del protocolo (roja). Los
    // códigos de error de RedPsi ("0", "00", "000") se pintan igual porque
    // para quien corrige son lo mismo: el ítem está malo.
    selectClass(val) {
      if (val === IDTEL_DEFAULT_ANSWER) return "";
      if (val === IDTEL_OMITTED_ANSWER) return "omitted";
      return "bad";
    },
    lineClass(val) {
      if (val === IDTEL_DEFAULT_ANSWER) return "";
      if (val === IDTEL_OMITTED_ANSWER) return "is-omitted";
      return "is-bad";
    },
    setItem(domainKey, rowIdx, value) {
      this.$set(this.uiState[domainKey], rowIdx, value);
      this.corrected = false;
      this.onSelectInteract();
    },
    onSelectInteract() {
      this.tryGuideAdvance();
    },
    isGuideActive(mIdx, dIdx, rowIdx) {
      const ws = this.corregirTestWorkspace;
      if (!ws) return false;
      const fieldIndex = 1 + idtelFlatIndex(mIdx, dIdx, rowIdx);
      return ws.isFieldGuideActive(fieldIndex);
    },
    tryGuideAdvance() {
      const ws = this.corregirTestWorkspace;
      if (ws && ws.dismissFieldGuide) ws.dismissFieldGuide();
    },
    async submit() {
      if (this.toolbarDisabled) return;
      this.loading = true;
      this.submitProgress = { current: 0, total: IDTEL_STEP_COUNT };
      try {
        const stepsAnswers = buildIdtelStepsAnswers(this.uiState, this.age);
        const data = await runIdtelCorrection({
          testValue: this.testValue,
          stepsAnswers,
          onStepComplete: (step) => {
            this.submitProgress = {
              current: step + 1,
              total: IDTEL_STEP_COUNT,
            };
          },
        });
        this.corrected = true;
        this.$emit("corrected", data);
      } catch (err) {
        const msg =
          (err && err.message) ||
          "No se pudo corregir IDTEL. Revisa los datos.";
        if (this.$toasted) this.$toasted.error(msg);
      } finally {
        this.loading = false;
        this.submitProgress = null;
      }
    },
    reset() {
      this.uiState = createIdtelUiState();
      this.corrected = false;
      this.loading = false;
      this.submitProgress = null;
    },
  },
};
</script>
